import type { CountryCode, LanguageCode } from '__generated__/graphql-types';

import config from 'js/app/config';

import type { SEOContentPageType } from 'bundles/unified-seo-content-common/types';

import _t from 'i18n!nls/unified-seo-content-common';

export const ITEMS_PER_CATEGORY_PAGE = 12;
export const DESKTOP_VIDEO_PLAYBACK_HEIGHT = 400;
export const DESKTOP_VIDEO_PLAYBACK_WIDTH = 750;
export const TABLET_VIDEO_PLAYBACK_HEIGHT = 300;
export const TABLET_VIDEO_PLAYBACK_WIDTH = 350;
export const HUB_PAGE_CONTENT_CONTAINER_MAX_WIDTH = 1440;

export const NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP = 3;

export const NUMBER_OF_ITEMS_PER_LINE_MOBILE = 2;
export const NUMBER_OF_ITEMS_PER_LINE_DESKTOP = 3;

export const ARTICLES_GRID_BREAKPOINTS_CONFIG = { md: 4 as const, sm: 6 as const, xs: 12 as const };

export const FEATURED_IMAGE_DIMENSIONS = {
  height: 680,
  width: 1500,
};

export const NAVIGATION_OFFSET = '65px';

export const getCannedCollectionMetadata = () => [
  {
    label: _t('Start your career'),
    href: '/career-academy',
    imageSrc: `${config.url.resource_assets}articles/hub-pages-images/start-your-career.png`,
    imageAlt: _t('A lady holding a file jacket and a coffee cup'),
  },
  {
    label: _t('Get a valuable credential'),
    href: '/certificates/prepare-for-a-certification',
    imageSrc: `${config.url.resource_assets}articles/hub-pages-images/get-a-valuable-credential.png`,
    imageAlt: _t('A lady using her laptop'),
  },
  {
    label: _t('Earn a top degree'),
    href: '/degrees',
    imageSrc: `${config.url.resource_assets}articles/hub-pages-images/earn-a-degree.png`,
    imageAlt: _t('A guy using his laptop on a couch'),
  },
];

export const ENTITY_TYPES = {
  course: 'course',
  project: 'project',
  s12n: 's12n',
  profS12n: 'profS12n',
  degree: 'degree',
  mastertrack: 'mastertrack',
} as const;

export const ENTITY_PATH_NAMES = {
  course: 'learn',
  s12n: 'specializations',
  profS12n: 'professional-certificates',
  project: 'projects',
  degree: 'degrees',
  mastertrack: 'mastertrack',
} as const;

export const getEntityTypeToLabelMapping = () => ({
  course: _t('COURSE'),
  project: _t('GUIDED PROJECT'),
  s12n: _t('SPECIALIZATION'),
  profS12n: _t('PROFESSIONAL CERTIFICATE'),
  degree: _t('DEGREE'),
  mastertrack: _t('MasterTrack® Certificate'),
});

// Corresponds to the content type id in Contentful
export const richTextEmbedTypes = {
  // youtubeEmbed: 'VideoTestimonialCard',
  articleDegreeRecommendations: 'ArticlesDegreeRecommendations',
  articleTable: 'Project',
  articleHighlight: 'ArticlesHighlight',
  articleVlpOrEntityEmbed: 'ArticlesVlpEmbed',
  articlePromoUnit: 'ArticlesPromoUnit',
  articleImageLink: 'ArticlesImageLink',
  faq: 'Faq',
  articleEmbeddedEntry: 'ArticlesVideoEntry',
} as const;

export const CONTENTFUL_DEGREE_RECS_CATEGORIES = {
  Business: 'Business',
  ComputerScience: 'Computer Science',
  DataScience: 'Data Science',
  DataAnalytics: 'Data Analytics',
  Management: 'Management',
  PublicHealth: 'Public Health',
  SocialSciences: 'Social Sciences',
  TopEuropean: 'Top European',
  TopIndian: 'Top Indian',
  TopLatam: 'Top Latin American',
};

export const CONTENTFUL_DEGREE_RECS_PRODUCT_TYPES = {
  Bachelors: "Bachelor's",
  Masters: "Master's",
};

// https://coursera.atlassian.net/browse/ACQU-1457
export const MAX_FAQS_TO_SHOW = 3;

export const SEO_CONTENT_PAGE_TYPE = {
  articles: 'articles',
  resources: 'resources',
} as const;

export const SEO_CONTENT_PAGE_SLUG: { [key in keyof typeof SEO_CONTENT_PAGE_TYPE]: string } = {
  articles: 'articles',
  resources: 'resources',
};

export const getSEOContentTitle = (pageType: SEOContentPageType): string => {
  switch (pageType) {
    case SEO_CONTENT_PAGE_TYPE.articles:
    default:
      return _t('Coursera Articles');
    case SEO_CONTENT_PAGE_TYPE.resources:
      return _t('Coursera Resources');
  }
};

export const DEFAULT_COUNTRY_CODE = 'global' as CountryCode;
export const DEFAULT_LANGUAGE_CODE = 'global' as LanguageCode;
export const DEFAULT_LOCALE_CODE = { languageCode: DEFAULT_LANGUAGE_CODE, countryCode: DEFAULT_COUNTRY_CODE };

export const DEFAULT_REGION = 'us';
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_LOCALE = { language: DEFAULT_LANGUAGE, region: DEFAULT_REGION };

export const LOCALIZED_HUB_PAGE_QUERY_FIELDS = {
  contentType: 'seoContentHubPage',
  fragmentFieldsName: 'SeoContentHubPageFields',
};

export const LOCALIZED_CONTENT_PAGE_QUERY_FIELDS = {
  contentType: 'seoContentContentPage',
  fragmentFieldsName: 'SeoContentContentPageFields',
};

export const CATEGORY_TYPE_NAME = {
  primaryCategory: 'ArticlesCategory',
  subCategory: 'ArticlesSubcategory',
};

/**
 * Constants representing page types for SEO content pages (e.g. articles)
 */
export const PAGE_TYPE = {
  content: 'Content',
  categoryHub: 'Category-level hub page',
  hubWithCategories: 'Hub page with categories',
};

export const PROMO_UNIT_TYPES = {
  courseraPlus: 'courseraPlus',
  courseraPlusPartnerLogos: 'courseraPlusPartnerLogos',
} as const;
